// src/contexts/PortfolioContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../services/firebase/config';
import { collection, getDocs, doc, getDoc, setDoc  } from 'firebase/firestore';
import { migratePortfolioData } from '../utils/portfolioMigration';

const PortfolioContext = createContext();

export function PortfolioProvider({ children }) {
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);

 const refreshPortfolios = async () => {
   if (!auth.currentUser?.email) return;
   try {
     // Load portfolios
     const portfoliosRef = collection(db, 'users', auth.currentUser.email, 'portfolios');
     const portfoliosSnap = await getDocs(portfoliosRef);
     const portfolioList = portfoliosSnap.docs.map(doc => ({
       id: doc.id,
       ...doc.data()
     }));

     if (!portfolioList.find(p => p.id === 'default')) {
       portfolioList.unshift({
         id: 'default',
         name: 'Ana Portföy',
         isDefault: true,
         icon: '💼'
       });
     }
     
     setPortfolios(portfolioList);

     // Load selected portfolio from settings
     const settingsRef = doc(db, 'users', auth.currentUser.email, 'settings', 'portfolio');
     const settingsSnap = await getDoc(settingsRef);
     const settings = settingsSnap.exists() ? settingsSnap.data() : null;
     
     if (settings?.selectedPortfolioId) {
       const selectedPort = portfolioList.find(p => p.id === settings.selectedPortfolioId);
       if (selectedPort) {
         setSelectedPortfolio(selectedPort);
       }
     }
   } catch (error) {
     console.error('Error refreshing portfolios:', error);
   }
 };

  const saveSelectedPortfolio = async (portfolioId) => {
    if (!auth.currentUser?.email) return;
    try {
      const settingsRef = doc(db, 'users', auth.currentUser.email, 'settings', 'portfolio');
      await setDoc(settingsRef, { selectedPortfolioId: portfolioId }, { merge: true });
    } catch (error) {
      console.error('Error saving selected portfolio:', error);
    }
  };

  const migrateDataIfNeeded = async (email, portfolioId) => {
    try {
      // Önce eski koleksiyonları kontrol et
      const userDoc = doc(db, 'users', email);
      const oldPositionsRef = collection(userDoc, 'positions');
      const oldPortfolioRef = collection(userDoc, 'portfolio');
      
      const [positionsSnapshot, portfolioSnapshot] = await Promise.all([
        getDocs(oldPositionsRef),
        getDocs(oldPortfolioRef)
      ]);

      // Eğer eski veriler varsa migrate et
      if (!positionsSnapshot.empty || !portfolioSnapshot.empty) {
        console.log('Migrating data to new structure...');
        await migratePortfolioData(email, portfolioId);
        console.log('Migration completed successfully');
      }
    } catch (error) {
      console.error('Migration error:', error);
    }
  };

  useEffect(() => {
    const loadPortfolios = async () => {
      if (!auth.currentUser?.email) {
        setLoading(false);
        return;
      }
      
      const userEmail = auth.currentUser.email; // Kullanıcı emailini useEffect içinde local bir değişkene al
      
      try {
        // Load portfolios
        const portfoliosRef = collection(db, 'users', userEmail, 'portfolios');
        const portfoliosSnap = await getDocs(portfoliosRef);
        const portfolioList = portfoliosSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Default portföyü ekle
        if (!portfolioList.find(p => p.id === 'default')) {
          portfolioList.unshift({
            id: 'default',
            name: 'Ana Portföy',
            isDefault: true,
            icon: '💼'
          });
        }

        setPortfolios(portfolioList);

        // Load selected portfolio from settings
        const settingsRef = doc(db, 'users', userEmail, 'settings', 'portfolio');
        const settingsSnap = await getDoc(settingsRef);
        const settings = settingsSnap.exists() ? settingsSnap.data() : null;
        
        let selectedPort;
        if (settings?.selectedPortfolioId) {
          selectedPort = portfolioList.find(p => p.id === settings.selectedPortfolioId);
          if (selectedPort) {
            setSelectedPortfolio(selectedPort);
            // Seçili portföy değiştiğinde migration'ı kontrol et
            await migrateDataIfNeeded(userEmail, selectedPort.id);
          } else {
            setSelectedPortfolio(portfolioList[0]);
            saveSelectedPortfolio(portfolioList[0].id);
          }
        } else {
          setSelectedPortfolio(portfolioList[0]);
          saveSelectedPortfolio(portfolioList[0].id);
          // İlk portföy seçildiğinde de migration'ı kontrol et
          await migrateDataIfNeeded(userEmail, portfolioList[0].id);
        }
      } catch (error) {
        console.error('Portföyler yüklenirken hata oluştu:', error);
        setPortfolios([]);
      } finally {
        setLoading(false);
      }
    };
   
    loadPortfolios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Boş bağımlılık dizisi ve eslint-disable ile uyarıyı bastır

  // Save selected portfolio whenever it changes
  useEffect(() => {
    if (selectedPortfolio?.id) {
      saveSelectedPortfolio(selectedPortfolio.id);
    }
  }, [selectedPortfolio?.id]);

  return (
    <PortfolioContext.Provider value={{
      portfolios,
      selectedPortfolio,
      setSelectedPortfolio,
      loading,
      refreshPortfolios
    }}>
      {children}
    </PortfolioContext.Provider>
  );
}

export function usePortfolio() {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
}